<script setup>
import { computed } from 'vue';
import BaseBubble from './Base.vue';
import { useI18n } from 'vue-i18n';
import { CONTENT_TYPES } from '../constants.js';
import { useMessageContext } from '../provider.js';
import { useInbox } from 'dashboard/composables/useInbox';

const { content, contentAttributes, contentType } = useMessageContext();
const { t } = useI18n();
const { isAWebWidgetInbox } = useInbox();

const formValues = computed(() => {
  if (contentType.value === CONTENT_TYPES.FORM) {
    const { items, submittedValues = [] } = contentAttributes.value;

    if (submittedValues.length) {
      return submittedValues.map(submittedValue => {
        const item = items.find(
          formItem => formItem.name === submittedValue.name
        );
        return {
          title: submittedValue.value,
          value: submittedValue.value,
          label: item?.label,
        };
      });
    }

    return [];
  }

  if (contentType.value === CONTENT_TYPES.INPUT_SELECT) {
    const [item] = contentAttributes.value?.submittedValues ?? [];
    if (!item) return [];

    return [
      {
        title: item.title,
        value: item.value,
        label: '',
      },
    ];
  }

  return [];
});

const headerMediaUrl = computed(() => {
  const url = contentAttributes.value?.templateParams?.headerUrl;
  if (!url) return null;

  if (url.includes('localhost:3000')) {
    return url.replace(/https?:\/\/localhost:3000/, window.location.origin);
  }
  return url;
});

const isPayment = computed(
  () =>
    contentType.value === CONTENT_TYPES.CARDS &&
    contentAttributes.value?.templateParams?.format === 'payment'
);

const paymentDetails = computed(() => contentAttributes.value?.payment || {});

const paymentImageUrl = computed(() => {
  const url = paymentDetails.value?.imageUrl || paymentDetails.value?.image_url;
  if (!url) return null;
  if (url.includes('localhost:3000')) {
    return url.replace(/https?:\/\/localhost:3000/, window.location.origin);
  }
  return url;
});

const paymentMethods = computed(() => paymentDetails.value?.methods || []);

const paymentAction = computed(() => {
  const configuredAction =
    paymentDetails.value?.actionText ||
    paymentDetails.value?.action_text ||
    contentAttributes.value?.items?.[0]?.actions?.[0]?.text;
  const hasPix = paymentMethods.value.some(
    method => method.toString().toLowerCase() === 'pix'
  );
  const genericPaymentAction =
    !configuredAction ||
    ['abrir detalhes do pagamento', 'order details'].includes(
      configuredAction.toString().trim().toLowerCase()
    );

  if (hasPix && genericPaymentAction) return 'Copiar código Pix';
  return configuredAction;
});

const paymentQuantityLabel = computed(
  () => `Quantidade: ${paymentDetails.value?.quantity || '1'}`
);

const paymentAmountLabel = computed(() => {
  const amount = paymentDetails.value?.amount;
  if (amount === null || amount === undefined || amount === '')
    return 'R$ 0,00';

  const numericAmount = Number(String(amount).replace(',', '.'));
  if (!Number.isFinite(numericAmount)) return `R$ ${amount}`;

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(numericAmount);
});

const paymentMethodLabel = method => {
  if (method === 'pix') return 'PIX';
  if (method === 'boleto') return 'Boleto';
  if (method === 'card') return 'Cartão';
  return method;
};

const paymentPayLabel = 'Pagar com';
const paymentTotalLabel = 'Total';

const buttonActions = computed(() => {
  const items = contentAttributes.value?.items || [];
  return items[0]?.actions || [];
});
</script>

<template>
  <BaseBubble class="px-0 py-0 overflow-hidden" data-bubble-name="form">
    <!-- Header Image -->
    <div v-if="headerMediaUrl && !isPayment" class="w-full">
      <img
        :src="headerMediaUrl"
        class="w-full object-cover max-h-48 rounded-t-md"
        alt="Header Media"
      />
    </div>

    <!-- Billing / payment card -->
    <div v-if="isPayment" class="px-4 pt-3">
      <div
        class="w-full rounded-xl border border-slate-200 bg-white !text-[#0f172a] dark:border-slate-600 dark:bg-slate-100 dark:!text-[#0f172a] overflow-hidden shadow-sm"
      >
        <div
          class="px-3 pt-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500"
        >
          {{ paymentDetails.title || 'Detalhes do pagamento' }}
        </div>
        <div class="flex gap-3 px-3 py-3 items-center">
          <img
            v-if="paymentImageUrl"
            :src="paymentImageUrl"
            class="size-14 rounded-lg object-cover bg-slate-100"
            alt="Imagem do pagamento"
          />
          <div
            v-else
            class="size-14 rounded-lg bg-slate-200 flex items-center justify-center text-slate-500"
            aria-hidden="true"
          >
            <span class="i-lucide-receipt-text size-6" />
          </div>
          <div class="min-w-0">
            <div class="font-semibold truncate">
              {{ paymentDetails.product || 'Fatura' }}
            </div>
            <div class="text-xs text-slate-500">
              {{ paymentQuantityLabel }}
            </div>
          </div>
        </div>
        <div class="mx-3 border-t border-dashed border-slate-300" />
        <div class="px-3 py-2 flex items-center justify-between text-xs">
          <span class="text-slate-500">{{ paymentPayLabel }}</span>
          <span
            class="flex flex-wrap items-center justify-end gap-2 font-medium uppercase"
          >
            <span
              v-for="method in paymentMethods"
              :key="method"
              class="inline-flex items-center text-slate-700"
            >
              {{ paymentMethodLabel(method) }}
            </span>
          </span>
        </div>
        <div class="mx-3 border-t border-slate-200" />
        <div class="px-3 py-3 flex items-center justify-between">
          <span class="font-semibold text-sm">{{ paymentTotalLabel }}</span>
          <span class="font-semibold text-sm">{{ paymentAmountLabel }}</span>
        </div>
      </div>
    </div>

    <!-- Body text -->
    <div class="px-4 pt-3 pb-2" :class="isPayment ? 'text-xs leading-5' : ''">
      <span
        v-dompurify-html="content"
        :title="content"
        class="block max-w-full whitespace-pre-line break-words [overflow-wrap:anywhere]"
      />

      <!-- Footer -->
      <div
        v-if="contentAttributes?.templateParams?.footer"
        class="mt-2 max-w-full whitespace-pre-line break-words text-xs opacity-70 uppercase tracking-wide [overflow-wrap:anywhere]"
      >
        {{ contentAttributes.templateParams.footer }}
      </div>
    </div>

    <div v-if="isPayment && paymentAction" class="px-4 pb-3">
      <button
        class="block w-full px-3 py-2 text-xs bg-n-brand/10 text-n-brand dark:text-n-brand-dark font-semibold rounded-md border border-n-brand/30 text-center"
      >
        {{ paymentAction }}
      </button>
    </div>

    <!-- Legacy Form Values (CSAT etc) -->
    <dl v-if="formValues.length" class="px-4 pb-3 mt-2">
      <template v-for="item in formValues" :key="item.title">
        <dt class="italic mt-2 text-sm opacity-80">
          {{ item.label || t('CONVERSATION.RESPONSE') }}
        </dt>
        <dd>{{ item.title }}</dd>
      </template>
    </dl>

    <!-- Interactive Carousel -->
    <div
      v-else-if="
        contentType === 'cards' &&
        contentAttributes?.templateParams?.format === 'carousel' &&
        contentAttributes?.items?.length
      "
      class="mt-3 px-4 pb-4 flex overflow-x-auto gap-3 snap-x pb-2"
    >
      <div
        v-for="(card, idx) in contentAttributes.items"
        :key="idx"
        class="shrink-0 w-60 bg-black/10 dark:bg-black/20 rounded-xl overflow-hidden shadow-sm flex flex-col snap-start border border-black/5 dark:border-white/5"
      >
        <img
          v-if="card.media_url || card.mediaUrl"
          :src="card.media_url || card.mediaUrl"
          class="w-full h-32 object-cover bg-black/5"
        />
        <div class="p-3 flex-1 flex flex-col">
          <div v-if="card.title" class="font-bold text-sm mb-1">
            {{ card.title }}
          </div>
          <div v-if="card.description" class="text-xs opacity-80 mb-2 flex-1">
            {{ card.description }}
          </div>
          <div
            class="text-[11px] font-bold opacity-60 flex items-center gap-1 mt-auto border-t border-black/10 dark:border-white/10 pt-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M5 12h14" />
              <path d="m12 5 7 7-7 7" />
            </svg>
            {{ card.actions?.[0]?.text || 'Ação' }}
          </div>
        </div>
      </div>
    </div>

    <!-- Interactive Buttons -->
    <div
      v-else-if="
        contentType === 'cards' &&
        ['buttons', 'quick_replies', 'personalized'].includes(
          contentAttributes?.templateParams?.format
        ) &&
        buttonActions.length
      "
      class="px-4 pb-4 flex flex-col gap-2 mt-2"
    >
      <div
        class="flex flex-col gap-2 pt-2 border-t border-black/10 dark:border-white/10"
      >
        <button
          v-for="(action, idx) in buttonActions"
          :key="idx"
          class="px-3 py-2 bg-black/5 hover:bg-black/10 dark:bg-black/20 dark:hover:bg-black/30 text-n-brand dark:text-n-brand-dark font-semibold rounded-md border border-black/10 dark:border-white/10 text-center transition-colors shadow-sm"
        >
          <span
            v-if="action.type === 'link'"
            class="i-lucide-link-2 size-4 inline-block align-middle"
            aria-hidden="true"
          />
          {{ action.text }}
        </button>
      </div>
    </div>

    <!-- Interactive List -->
    <div
      v-else-if="!isPayment && contentAttributes?.items?.length"
      class="mt-1 px-4 pb-4 flex flex-col gap-2"
    >
      <div class="mt-2 pt-3 border-t border-black/10 dark:border-white/10">
        <div
          class="text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-1 opacity-90"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="opacity-70"
          >
            <line x1="8" y1="6" x2="21" y2="6" />
            <line x1="8" y1="12" x2="21" y2="12" />
            <line x1="8" y1="18" x2="21" y2="18" />
            <line x1="3" y1="6" x2="3.01" y2="6" />
            <line x1="3" y1="12" x2="3.01" y2="12" />
            <line x1="3" y1="18" x2="3.01" y2="18" />
          </svg>
          {{
            contentAttributes.buttonText ||
            contentAttributes.templateParams?.listButtonText ||
            'Opções'
          }}
        </div>

        <div class="flex flex-col gap-2">
          <div
            v-for="(option, idx) in contentAttributes.items"
            :key="idx"
            class="px-3 py-2 bg-black/10 dark:bg-black/20 rounded-md border border-black/5 shadow-sm"
          >
            <div class="font-semibold text-sm flex items-start gap-2">
              <span
                class="text-xs bg-black/20 dark:bg-black/40 w-5 h-5 flex items-center justify-center rounded-full shrink-0 mt-0.5"
              >
                {{ idx + 1 }}
              </span>
              <span>{{
                option.title || option.label || `Opção ${idx + 1}`
              }}</span>
            </div>
            <div v-if="option.description" class="text-xs opacity-80 mt-1 pl-7">
              {{ option.description }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="isAWebWidgetInbox" class="px-4 py-3 font-medium">
      {{ t('CONVERSATION.NO_RESPONSE') }}
    </div>
  </BaseBubble>
</template>
